import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Mail, Send, Square } from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { notificar } from '@/shared/ui/avisos';
import type { AvanceDelCorreo, PorEnviar } from './tiposDelProcesoSri';

export function ColaDeCorreos({ avance }: { avance: AvanceDelCorreo | null }) {
  const [buscar, setBuscar] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [elegidos, setElegidos] = useState<number[]>([]);
  const clienteDeConsultas = useQueryClient();

  const corriendo = avance?.enCurso === true;

  const pendientes = useQuery({
    queryKey: ['correos-pendientes', buscar, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<PorEnviar>>('/proceso-sri/correos/pendientes', {
        query: { buscar, pagina, tamanio },
      }),

    refetchInterval: corriendo ? 4000 : false,
  });

  function refrescar() {
    void clienteDeConsultas.invalidateQueries({ queryKey: ['correos-pendientes'] });
    void clienteDeConsultas.invalidateQueries({ queryKey: ['proceso-sri'] });
  }

  const arrancar = useMutation({
    mutationFn: (facturas: number[]) =>
      api.post<AvanceDelCorreo>(
        '/proceso-sri/correos/cola',
        facturas.length > 0 ? facturas : undefined,
      ),
    onSuccess: (a) => {
      notificar.exito(`EN COLA: ${a.total.toLocaleString('es-EC')} COMPROBANTES`);
      setElegidos([]);
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo arrancar'),
  });

  const detener = useMutation({
    mutationFn: () => api.post<void>('/proceso-sri/correos/cola/detener'),
    onSuccess: () => {
      notificar.exito('LA COLA SE DETUVO. LO QUE YA SALIÓ NO SE VUELVE A MANDAR.');
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo detener'),
  });

  const enPantalla = pendientes.data?.contenido ?? [];
  const todosMarcados = enPantalla.length > 0 && enPantalla.every((f) => elegidos.includes(f.id));

  const columnas: Columna<PorEnviar>[] = [
    {
      clave: 'elegir',
      encabezado: '',
      contenido: (f) => (
        <input
          type="checkbox"
          checked={elegidos.includes(f.id)}
          onChange={() =>
            setElegidos((antes) =>
              antes.includes(f.id) ? antes.filter((otro) => otro !== f.id) : [...antes, f.id],
            )
          }
          aria-label={`Elegir el comprobante de ${f.cliente}`}
          className="size-4 accent-marca-600"
        />
      ),
    },
    {
      clave: 'cliente',
      encabezado: 'Cliente',
      contenido: (f) => (
        <span className="block">
          <span className="block truncate font-medium text-armazon-700">{f.cliente}</span>
          <span className="block font-mono text-[10px] text-armazon-400">{f.identificacion}</span>
        </span>
      ),
    },
    {
      clave: 'correo',
      encabezado: 'Correo',
      contenido: (f) => <span className="truncate text-xs text-armazon-600">{f.correo}</span>,
    },
    { clave: 'periodo', encabezado: 'Período', contenido: (f) => f.periodo },
    {
      clave: 'numero',
      encabezado: 'Nº factura',
      contenido: (f) => <span className="font-mono text-xs">{f.numero}</span>,
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      contenido: (f) => <span className="importe">${f.total.toFixed(2)}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      {avance && <AvanceDeLaCola avance={avance} />}

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-armazon-200 bg-white p-4">
        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-armazon-600">
          <Mail className="size-4 text-armazon-400" aria-hidden />
          {(pendientes.data?.totalElementos ?? 0).toLocaleString('es-EC')} esperan su comprobante
        </span>

        <label className="flex items-center gap-2 text-[11px] uppercase text-armazon-500">
          <input
            type="checkbox"
            checked={todosMarcados}
            onChange={() =>
              setElegidos((antes) =>
                todosMarcados
                  ? antes.filter((id) => !enPantalla.some((f) => f.id === id))
                  : [...new Set([...antes, ...enPantalla.map((f) => f.id)])],
              )
            }
            aria-label="Elegir todos los de esta página"
            className="size-4 accent-marca-600"
          />
          Toda la página
        </label>

        {elegidos.length > 0 && (
          <button
            type="button"
            onClick={() => setElegidos([])}
            className="text-[11px] uppercase text-armazon-400 underline hover:text-armazon-600"
          >
            Quitar los {elegidos.length} elegidos
          </button>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {corriendo ? (
            <button
              type="button"
              onClick={() => detener.mutate()}
              disabled={detener.isPending}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 text-[11px] font-bold uppercase tracking-wide text-red-700 transition hover:bg-red-100 disabled:opacity-40"
            >
              <Square className="size-4" aria-hidden />
              Detener
            </button>
          ) : (
            <button
              type="button"
              onClick={() => arrancar.mutate(elegidos)}
              disabled={arrancar.isPending || (pendientes.data?.totalElementos ?? 0) === 0}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-marca-600 px-4 text-[11px] font-bold uppercase tracking-wide text-white transition hover:bg-marca-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {arrancar.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Send className="size-4" aria-hidden />
              )}
              {elegidos.length > 0
                ? `Enviar ${elegidos.length.toLocaleString('es-EC')} elegidos`
                : `Enviar los ${(pendientes.data?.totalElementos ?? 0).toLocaleString('es-EC')}`}
            </button>
          )}
        </div>
      </div>

      <Tabla
        datos={pendientes.data}
        cargando={pendientes.isLoading}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(nuevo) => {
          setTamanio(nuevo);
          setPagina(0);
        }}
        iconoVacio={Mail}
        mensajeVacio="No queda ningún comprobante por entregar"
        entidad="POR ENTREGAR"
        herramientas={
          <CampoDeBusqueda
            valor={buscar}
            alCambiar={(texto) => {
              setBuscar(texto);
              setPagina(0);
            }}
            marcador="Nombre, cédula, correo o nº de comprobante"
          />
        }
      />
    </div>
  );
}

function AvanceDeLaCola({ avance }: { avance: AvanceDelCorreo }) {
  const hechos = avance.enviados + avance.fallidos;
  const porcentaje = avance.total > 0 ? Math.round((hechos / avance.total) * 100) : 0;

  return (
    <div className="rounded-2xl border border-armazon-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <span
          className={`inline-flex items-center gap-2 rounded-lg border px-2 py-1 text-[11px] font-bold uppercase tracking-wide ${
            avance.enCurso
              ? 'border-marca-200 bg-marca-50 text-marca-700'
              : 'border-armazon-200 bg-armazon-50 text-armazon-600'
          }`}
        >
          {avance.enCurso && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
          {avance.enCurso ? 'Enviando' : 'Último envío'}
        </span>

        <span className="text-2xl font-black tabular-nums text-marca-700">{porcentaje}%</span>

        <span className="font-mono text-[11px] text-armazon-500">
          {hechos.toLocaleString('es-EC')} / {avance.total.toLocaleString('es-EC')}
        </span>

        <span className="text-[11px] uppercase text-marca-700">
          {avance.enviados.toLocaleString('es-EC')} entregados
        </span>

        {avance.fallidos > 0 && (
          <span className="text-[11px] uppercase text-red-700">
            {avance.fallidos.toLocaleString('es-EC')} no salieron
          </span>
        )}

        {avance.iniciadoPor && (
          <span className="ml-auto text-[10px] uppercase text-armazon-500">
            Lanzado por {avance.iniciadoPor}
          </span>
        )}
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-armazon-100">
        <div
          className="h-full rounded-full bg-marca-500 transition-all duration-700"
          style={{ width: `${porcentaje}%` }}
        />
      </div>
    </div>
  );
}
